/**
 * useGoHomeShortcut — Iter 265.3
 *
 * Gmail-style two-key sequence: press `g` then `h` (within ~1.2s) to
 * navigate to the app's home route with the same `reset` state a logo
 * click sends, which clears filters and bulk selection on the dashboard.
 *
 * Ignores presses while an input/textarea/select/contenteditable field is
 * focused so we don't hijack normal typing. Also skipped while a modifier
 * key is held.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const SEQUENCE_WINDOW_MS = 1200;

function isEditableTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // Radix / shadcn combobox listbox items shouldn't hijack either.
  if (el.getAttribute?.('role') === 'combobox') return true;
  return false;
}

export default function useGoHomeShortcut(homeLink) {
  const navigate = useNavigate();
  const primedAt = useRef(0);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        primedAt.current = 0;
        return;
      }
      if (isEditableTarget(document.activeElement)) {
        primedAt.current = 0;
        return;
      }
      const key = (e.key || '').toLowerCase();
      const now = Date.now();
      if (key === 'g') {
        primedAt.current = now;
        return;
      }
      if (key === 'h' && primedAt.current && now - primedAt.current <= SEQUENCE_WINDOW_MS) {
        primedAt.current = 0;
        e.preventDefault();
        navigate(homeLink, { state: { reset: Date.now() } });
        return;
      }
      // Any other key breaks the sequence.
      primedAt.current = 0;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, homeLink]);
}
